import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField
} from '@mui/material';

// Add this constant
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Define the structure for a voice object (based on ElevenLabs API)
interface Voice {
  voice_id: string;
  name: string;
  // Add other relevant fields if needed, e.g., category, labels
}

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  currentSettings: {
    voiceId: string;
    stability: number;
    similarityBoost: number;
  };
  onSave: (newSettings: {
    voiceId: string;
    stability: number;
    similarityBoost: number;
  }) => void;
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
  currentSettings,
  onSave,
}) => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loadingVoices, setLoadingVoices] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Local state for editing settings within the dialog
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(currentSettings.voiceId);
  const [stability, setStability] = useState<number>(currentSettings.stability);
  const [similarityBoost, setSimilarityBoost] = useState<number>(currentSettings.similarityBoost);

  // Fetch voices when the dialog opens
  useEffect(() => {
    if (open) {
      const fetchVoices = async () => {
        setLoadingVoices(true);
        setError(null);
        try {
          const response = await fetch(`${API_BASE_URL}/api/voices`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          if (data.voices && Array.isArray(data.voices)) {
            setVoices(data.voices);
            // Ensure the current voice ID is still valid, otherwise reset to default/first?
            if (!data.voices.some((v: Voice) => v.voice_id === currentSettings.voiceId)) {
              console.warn('Current voice ID not found in fetched voices, resetting...');
              // Optionally reset to the first voice or a default
              if (data.voices.length > 0) {
                 setSelectedVoiceId(data.voices[0].voice_id);
              } 
            }
          } else {
            throw new Error('Invalid voice data format received');
          }
        } catch (err) {
          console.error('Failed to fetch voices:', err);
          setError(err instanceof Error ? err.message : 'Failed to load voices');
        } finally {
          setLoadingVoices(false);
        }
      };
      fetchVoices();
    }
  }, [open, currentSettings.voiceId]); // Re-fetch if dialog opens or initial voiceId changes

  // Update local state when currentSettings prop changes (e.g., saved externally)
  useEffect(() => {
    setSelectedVoiceId(currentSettings.voiceId);
    setStability(currentSettings.stability);
    setSimilarityBoost(currentSettings.similarityBoost);
  }, [currentSettings]);

  const handleSave = () => {
    onSave({ voiceId: selectedVoiceId, stability, similarityBoost });
    onClose();
  };

  // Find the currently selected voice object for Autocomplete value
  const selectedVoiceObject = voices.find(v => v.voice_id === selectedVoiceId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Voice Settings</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            id="voice-autocomplete"
            options={voices}
            getOptionLabel={(option) => option.name}
            value={selectedVoiceObject}
            onChange={(event, newValue) => {
              setSelectedVoiceId(newValue ? newValue.voice_id : '');
            }}
            isOptionEqualToValue={(option, value) => option.voice_id === value.voice_id}
            loading={loadingVoices}
            disabled={loadingVoices || voices.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Voice"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingVoices ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom>Stability</Typography>
          <Slider
            value={stability}
            onChange={(e, newValue) => setStability(newValue as number)}
            aria-labelledby="stability-slider"
            valueLabelDisplay="auto"
            step={0.01}
            marks
            min={0}
            max={1}
          />
        </Box>

        <Box>
          <Typography gutterBottom>Similarity Boost</Typography>
          <Slider
            value={similarityBoost}
            onChange={(e, newValue) => setSimilarityBoost(newValue as number)}
            aria-labelledby="similarity-boost-slider"
            valueLabelDisplay="auto"
            step={0.01}
            marks
            min={0}
            max={1}
          />
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loadingVoices}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 